import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import type { Mutation, Query } from "@tanstack/react-query";
import { useAppStore } from "@/lib/store/app";
import enUS from "@/translations/en-US.json";
import deDE from "@/translations/de-DE.json";

// Loosely typed on purpose: the two locale files aren't structurally
// identical (JSON module types), and all we need here is one nested string.
const translations: Record<string, any> = {
  "en-US": enUS,
  "de-DE": deDE,
};

function getGenericErrorMessage(): string {
  const locale = useAppStore.getState().locale;
  return (
    translations[locale]?.common?.sideNav?.errors?.somethingWentWrong ??
    "Something went wrong"
  );
}

// axios-client.ts's response interceptor already rewrites API failures into
// `Error`s with a curated, user-safe message (see handleAxiosError) — prefer
// that over the generic string when available.
function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return getGenericErrorMessage();
}

// query/mutation keys are arbitrary caller-supplied values (BigInt, circular
// refs, etc.) and JSON.stringify can throw on them — fall back to String()
// so a key we can't serialize never breaks error reporting.
function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

// React-query keys carry whatever identifiers the hooks were given — email
// addresses and other person-referencing values included — so the raw key must
// never reach Sentry as-is. Mask email-shaped values and cap the length so a
// huge key can't inflate the event or leak more context than is needed to
// identify the failing query.
const KEY_MAX_LENGTH = 256;
const EMAIL_IN_KEY =
  /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}(?="|'|\\|[,\s\]}])/gi;

function scrubQueryKey(value: unknown): string {
  const serialized = safeStringify(value);
  const scrubbed = serialized.replace(EMAIL_IN_KEY, "[redacted]");
  return scrubbed.length > KEY_MAX_LENGTH
    ? `${scrubbed.slice(0, KEY_MAX_LENGTH)}…`
    : scrubbed;
}

/** The contract `_tag` of a typed intent failure, if the error carries one. */
function errorTag(error: unknown): unknown {
  return typeof error === "object" && error !== null && "_tag" in error
    ? error._tag
    : undefined;
}

/**
 * Global QueryCache onError: every failed query is reported to Sentry and
 * surfaced to the user via toast, unless the query sets
 * `meta: { suppressGlobalError: true }` (e.g. a background/best-effort fetch
 * with its own inline degraded-state UI, so a global toast would be noise
 * on top of what the component already renders), or lists the failure's
 * contract tag in `meta.inlineErrorTags` because the component renders that
 * failure as its own state.
 */
export function handleGlobalQueryError(
  error: Error,
  query: Query<unknown, unknown, unknown, readonly unknown[]>,
): void {
  if (query.meta?.suppressGlobalError === true) return;
  const inlineErrorTags = query.meta?.inlineErrorTags;
  if (
    Array.isArray(inlineErrorTags) &&
    inlineErrorTags.includes(errorTag(error))
  ) {
    return;
  }

  Sentry.captureException(error, {
    tags: { source: "react-query-query" },
    extra: { queryKey: scrubQueryKey(query.queryKey) },
  });

  // Keyed by message so simultaneous failures collapse into one toast per
  // distinct error (e.g. going offline fails several queries at once) while
  // genuinely different errors still each get shown.
  const message = getErrorMessage(error);
  toast.error(message, { id: message });
}

/**
 * Global MutationCache onError: every failed mutation is reported to
 * Sentry, unless opted out via `meta: { suppressGlobalError: true }`.
 *
 * No global toast here — a rejected mutation always propagates to its
 * caller (via `onError` or a thrown rejection from `mutateAsync`/`mutate`),
 * so the calling component owns the user-facing signal. A global toast on
 * top would double up with the component's own handling. This differs from
 * queries, which have no such caller to fall back on and so keep the global
 * toast.
 */
export function handleGlobalMutationError(
  error: unknown,
  _variables: unknown,
  _context: unknown,
  mutation: Mutation<unknown, unknown, unknown, unknown>,
): void {
  if (mutation.options.meta?.suppressGlobalError === true) return;

  Sentry.captureException(error, {
    tags: { source: "react-query-mutation" },
    extra: {
      mutationKey: scrubQueryKey(mutation.options.mutationKey ?? null),
    },
  });
}
